import type { PxRect } from './camera';
import type { Theme } from './demo';

export type { PxRect };

/**
 * The presenter (webcam) bubble's layout — pure geometry, shared by the
 * player (`apps/player/src/presenter.ts`, Watch mode only) and the MP4/social
 * exporter (`services/pipeline/src/export/presenter.ts`), the same way
 * `camera.ts` is shared for the step camera. No zod, so the player can import
 * it from `@mado/schema/presenter` inside its 60KB budget.
 *
 * `Theme.presenter` is optional and additive: absent means "shown, bottom
 * right, medium, circle" (`DEFAULT_PRESENTER_SETTINGS`) whenever a step has a
 * clip — the player has no zod to fill that default at parse time, so every
 * reader resolves it through `resolvePresenterSettings` rather than reading
 * `theme.presenter` directly.
 */

export type PresenterCorner = 'br' | 'bl' | 'tr' | 'tl';
export type PresenterSize = 's' | 'm' | 'l';
export type PresenterShape = 'circle' | 'rounded';

export type PresenterSettings = NonNullable<Theme['presenter']>;

export const DEFAULT_PRESENTER_SETTINGS: PresenterSettings = {
  show: true,
  corner: 'br',
  size: 'm',
  shape: 'circle',
};

/** Absent (older documents, or a document with no presenter clips at all) is
 *  exactly `DEFAULT_PRESENTER_SETTINGS`; a partial object (defensive — every
 *  key already carries a zod default once parsed) is filled the same way. */
export function resolvePresenterSettings(
  presenter: Theme['presenter'] | null | undefined,
): PresenterSettings {
  return { ...DEFAULT_PRESENTER_SETTINGS, ...(presenter ?? {}) };
}

// `PxRect` (a rect in the stage's own pixels — viewport CSS px in the player,
// output px in the exporter) is `camera.ts`'s own type, re-exported above:
// the phone story's picture/caption rects and this bubble's rect are the same
// shape, and the player already imports both from `@mado/schema/camera` in
// the same call sites this module's callers need it from.

/** The bubble's diameter as a fraction of the stage's shorter side. Tuned so
 *  `m` reads as "a person in the corner of a video call", not a postage stamp
 *  on a phone-sized embed nor a face blocking a third of a small preview. */
export const PRESENTER_SIZE_FRACTION: Record<PresenterSize, number> = {
  s: 0.15,
  m: 0.22,
  l: 0.3,
};
/** Never smaller than a touch target, never so large it dominates a tiny embed. */
export const PRESENTER_MIN_PX = 56;
export const PRESENTER_MAX_PX = 240;
/** Kept clear of the stage edge, as a fraction of the stage's shorter side. */
export const PRESENTER_MARGIN_FRACTION = 0.035;
export const PRESENTER_MARGIN_MIN_PX = 10;

export interface PresenterBubble {
  rect: PxRect;
  /** The corner actually used — may differ from `settings.corner` when that
   *  corner collided with something in `avoid` (see `presenterBubble`). */
  corner: PresenterCorner;
  shape: PresenterShape;
}

/** Preference order once the author's own corner is unavailable: the other
 *  corner on the same edge, then the diagonal opposite, then the last one. */
const CORNER_FALLBACK: Record<PresenterCorner, PresenterCorner[]> = {
  br: ['br', 'bl', 'tr', 'tl'],
  bl: ['bl', 'br', 'tl', 'tr'],
  tr: ['tr', 'tl', 'br', 'bl'],
  tl: ['tl', 'tr', 'bl', 'br'],
};

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

function rectAt(
  corner: PresenterCorner,
  stage: { w: number; h: number },
  size: number,
  margin: number,
): PxRect {
  const x = corner === 'bl' || corner === 'tl' ? margin : stage.w - margin - size;
  const y = corner === 'tl' || corner === 'tr' ? margin : stage.h - margin - size;
  return { x, y, w: size, h: size };
}

function overlaps(a: PxRect, b: PxRect): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

/**
 * Where the bubble sits on a stage of `stage.w x stage.h` pixels: the
 * author's corner, sized from `settings.size`, unless it collides with
 * something in `avoid` (the chrome bar's band, the step's target/tooltip, a
 * story's caption band, the social cut's hook band) — then the next corner in
 * `CORNER_FALLBACK` that is clear, or the author's own corner regardless if
 * all four collide (a bubble has to go somewhere; better seen over the target
 * than not shown at all). `avoid` rects are in the same stage-pixel space as
 * the returned rect — the caller (player: viewport CSS px; exporter: the
 * stage's own output px, before it is composited onto a social canvas)
 * projects them there first.
 */
export function presenterBubble(
  stage: { w: number; h: number },
  settings: Pick<PresenterSettings, 'corner' | 'size' | 'shape'>,
  avoid: readonly PxRect[] = [],
): PresenterBubble {
  const short = Math.min(stage.w, stage.h);
  const size = clamp(
    short * PRESENTER_SIZE_FRACTION[settings.size],
    PRESENTER_MIN_PX,
    Math.max(PRESENTER_MIN_PX, Math.min(PRESENTER_MAX_PX, short * 0.6)),
  );
  const margin = Math.max(PRESENTER_MARGIN_MIN_PX, short * PRESENTER_MARGIN_FRACTION);
  const order = CORNER_FALLBACK[settings.corner];
  let corner = order[0]!;
  let rect = rectAt(corner, stage, size, margin);
  for (const c of order) {
    const r = rectAt(c, stage, size, margin);
    if (!avoid.some((a) => overlaps(r, a))) {
      corner = c;
      rect = r;
      break;
    }
  }
  return { rect, corner, shape: settings.shape };
}
